import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { voucherService } from '../../services/vouchers/voucherService';
import {
    Search,
    MapPin,
    Calendar,
    User,
    ArrowRight,
    Ticket,
    Plane,
    Loader2,
    Inbox
} from 'lucide-react';

const GlobalSearchResults = ({ searchTerm, onClose }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [results, setResults] = useState({
        vouchers: [],
        features: [] // Added features array
    });

    // Define available features for search
    const APP_FEATURES = [
        { id: 'f1', name: 'Flight Tracking', path: '/flight-tracking', icon: Plane, keywords: ['flight', 'track', 'live', 'air'] },
        { id: 'f2', name: 'Train Tracking', path: '/train-tracking', icon: Calendar, keywords: ['train', 'rail', 'track', 'status'] }, // Using Calendar as placeholder or import Train
        { id: 'f3', name: 'Forex Orders', path: '/forexOrder', icon: MapPin, keywords: ['forex', 'currency', 'money', 'exchange'] },
        { id: 'f4', name: 'Create Voucher', path: '/create-voucher', icon: Ticket, keywords: ['create', 'voucher', 'booking', 'new'] },
        { id: 'f5', name: 'Saved Vouchers', path: '/voucher', icon: Ticket, keywords: ['voucher', 'list', 'history', 'bookings'] },
        { id: 'f6', name: 'Customer Feedback', path: '/feedback', icon: User, keywords: ['feedback', 'review', 'rate', 'customer'] },
    ];

    useEffect(() => {
        const fetchResults = async () => {
            if (!searchTerm || searchTerm.length < 2) return;

            setLoading(true);
            try {
                const lowerTerm = searchTerm.toLowerCase();

                // 1. Search Features (Local)
                const matchingFeatures = APP_FEATURES.filter(feature =>
                    feature.name.toLowerCase().includes(lowerTerm) ||
                    feature.keywords.some(k => k.includes(lowerTerm))
                );

                // 2. Fetch Vouchers (API)
                const response = await voucherService.getVouchers();

                let filteredVouchers = [];
                if (response) {
                    const allBookings = Array.isArray(response) ? response : (Array.isArray(response.vouchers) ? response.vouchers : response.data);

                    if (Array.isArray(allBookings)) {
                        filteredVouchers = allBookings.filter(booking => {
                            const guestName = booking.guest_information?.guest_name?.toLowerCase() || '';
                            const city = booking.tour_info?.destinations?.[0]?.city?.toLowerCase() || '';
                            const pnr = booking.pnr_number?.toLowerCase() || ''; // Adjusted if field name differs
                            const id = booking._id?.toLowerCase() || '';
                            // Check for booking_id as well
                            const bookingId = booking.booking_id?.toLowerCase() || '';

                            return guestName.includes(lowerTerm) ||
                                city.includes(lowerTerm) ||
                                pnr.includes(lowerTerm) ||
                                bookingId.includes(lowerTerm) ||
                                id.includes(lowerTerm);
                        });
                    }
                }

                setResults({
                    vouchers: filteredVouchers,
                    features: matchingFeatures
                });

            } catch (error) {
                console.error("Global search error:", error);
            } finally {
                setLoading(false);
            }
        };

        const timeoutId = setTimeout(() => {
            fetchResults();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);



    const hasResults = results.vouchers.length > 0 || (results.features && results.features.length > 0);

    return (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 min-h-[60vh] p-6 animate-fade-in">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <Search className="text-primary" size={24} />
                    Search Results for "<span className="text-primary">{searchTerm}</span>"
                </h2>
                <button
                    onClick={onClose}
                    className="text-sm font-semibold text-gray-500 hover:text-gray-800 underline decoration-gray-300 underline-offset-4"
                >
                    Clear Search
                </button>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                    <Loader2 size={40} className="animate-spin mb-4 text-primary" />
                    <p>Searching everywhere...</p>
                </div>
            ) : !hasResults ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                    <Inbox size={48} className="mb-4 opacity-50" />
                    <p className="text-lg font-medium">No results found</p>
                    <p className="text-sm">Try searching for a guest name, city, or PNR.</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {/* Vouchers Section */}
                    {results.vouchers.length > 0 && (
                        <section>
                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                                <Ticket size={16} />
                                Vouchers / Bookings ({results.vouchers.length})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {results.vouchers.map((booking) => (
                                    <div
                                        key={booking._id}
                                        onClick={() => {
                                            // Close modal first
                                            if (onClose) onClose();
                                            // Navigate to specific booking detail or list
                                            // Assuming we go to voucher page and functionality to highlight/open might be needed later
                                            // Navigate to dashboard with voucher tab active
                                            navigate('/dashboard', { state: { activeTab: 'voucher' } });
                                        }}
                                        className="bg-slate-50 p-4 rounded-xl border border-slate-100 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer group"
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-lg">
                                                    {booking.guest_information?.guest_name?.charAt(0) || 'G'}
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-gray-900 group-hover:text-primary transition-colors">
                                                        {booking.guest_information?.guest_name || 'Guest'}
                                                    </h4>
                                                    <p className="text-xs text-gray-500">ID: {booking.booking_id || booking._id?.slice(-6)}</p>
                                                </div>
                                            </div>
                                            <ArrowRight size={16} className="text-gray-300 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                                        </div>

                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center gap-2 text-gray-600">
                                                <MapPin size={14} className="text-gray-400" />
                                                <span>{booking.tour_info?.destinations?.[0]?.city || 'Unknown City'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-gray-600">
                                                <Calendar size={14} className="text-gray-400" />
                                                <span>{booking.tour_info?.start_date ? new Date(booking.tour_info.start_date).toLocaleDateString() : 'TBD'}</span>
                                            </div>
                                            {booking.vehicle_info && (
                                                <div className="flex items-center gap-2 text-gray-600">
                                                    <Plane size={14} className="text-gray-400" />
                                                    <span>{booking.vehicle_info.vehicle_type || 'Transport'}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            )}
        </div>
    );
};

export default GlobalSearchResults;
